import { AxiosError } from 'axios';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function clamp(val: number, min: number, max: number) {
  return val > max ? max : val < min ? min : val;
}

export function toPercentage(val: number, min: number, max: number) {
  if (val < min) {
    val = min;
  } else if (val > max) {
    val = max;
  }

  const percentage = (val - min) / (max - min);

  return +percentage.toFixed(2);
}

export function getEuropeanTimezones(): { label: string; value: string }[] {
  const allTimeZones = Intl.supportedValuesOf('timeZone');

  return allTimeZones
    .filter((zone: string) => zone.startsWith('Europe/'))
    .map((zone: string) => ({
      value: zone,
      label: zone,
    }));
}

export const formatValueOrDefault = (
  value?: string | number | null,
  label?: string,
  defaultValue: string = '--'
): string => {
  return value != null ? `${value}${label ? ` ${label}` : ''}` : defaultValue;
};

export function isValidDate(d?: string | number | Date): boolean {
  if (d === undefined || d === null) return false;

  const date = d instanceof Date ? d : new Date(d);
  return !isNaN(date.getTime());
}

export const formatPrice = (price: number | null | undefined): string => {
  if (price === null || price === undefined) return '-';
  return `€${(price / 100).toFixed(2)}`;
};

export const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const isNotFoundError = (error: unknown): boolean =>
  error instanceof AxiosError && error.response?.status === 404;

export function buildFacetOptions<T>(
  items: T[],
  keyFn: (item: T) => string
): { label: string; value: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const item of items) {
    const key = keyFn(item);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return Array.from(counts.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, count]) => ({ label: key, value: key, count }));
}
